import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import CalendarGrid from '../components/calendar/CalendarGrid';
import CalendarLegend from '../components/calendar/CalendarLegend';
import './Calendar.css';

function Calendar() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [holidays, setHolidays] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [loading, setLoading] = useState(true);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthLabel = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate the first and last visible dates for the month grid
      const firstOfMonth = new Date(year, month, 1);
      const lastOfMonth = new Date(year, month + 1, 0);
      const startDate = firstOfMonth.toISOString().split('T')[0];
      const endDate = lastOfMonth.toISOString().split('T')[0];

      const [countriesRes, eventsRes] = await Promise.allSettled([
        api.get('/countries'),
        api.get(`/calendar/events?start=${startDate}&end=${endDate}`),
      ]);

      setCountries(countriesRes.status === 'fulfilled' ? (countriesRes.value.countries || []) : []);

      // Split events into holidays and time off for the existing CalendarGrid
      const events = eventsRes.status === 'fulfilled' ? (eventsRes.value.events || []) : [];
      setHolidays(events.filter(e => e.type === 'holiday'));
      setTimeOff(events.filter(e => e.type === 'time_off'));
    } catch (err) {
      // Don't loop -- just show empty calendar
      setCountries([]);
      setHolidays([]);
      setTimeOff([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Filter holidays by selected country
  const filteredHolidays = selectedCountry === 'all'
    ? holidays
    : holidays.filter(h => h.country_id === selectedCountry);

  return (
    <div className="calendar-page">
      <div className="calendar-header">
        <div className="calendar-header-left">
          <h1>Calendar</h1>
        </div>
        <div className="calendar-header-right">
          <select
            className="country-filter"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            <option value="all">All Countries</option>
            {countries.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="calendar-nav">
        <button className="calendar-nav-btn" onClick={goToPreviousMonth}>
          <ChevronLeft size={18} />
        </button>
        <h2 className="calendar-month-label">{monthLabel}</h2>
        <button className="calendar-nav-btn" onClick={goToNextMonth}>
          <ChevronRight size={18} />
        </button>
        <button className="calendar-today-btn" onClick={goToToday}>
          Today
        </button>
      </div>

      <CalendarLegend countries={countries} selectedCountry={selectedCountry} />

      {loading ? (
        <div className="calendar-loading">Loading calendar...</div>
      ) : (
        <CalendarGrid
          year={year}
          month={month}
          holidays={filteredHolidays}
          timeOff={timeOff}
          userCountryId={user?.country?.id}
        />
      )}
    </div>
  );
}

export default Calendar;
